import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useOperator } from '../../contexts/OperatorContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import formatOrderId from '../../utils/formatOrderId';

const { FiDollarSign, FiArrowDown, FiArrowUp, FiClock, FiCheckCircle, FiXCircle, FiFilter, FiDownload } = FiIcons;

const OperatorWallet = () => {
  const { operatorProfile, transactions, fetchTransactions, walletBalance } = useOperator();
  const [transactionType, setTransactionType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const loadTransactions = async () => {
      setLoading(true);
      await fetchTransactions();
      setLoading(false);
    };
    
    loadTransactions();
  }, [fetchTransactions]);
  
  // Filter transactions based on type and date range
  const filteredTransactions = transactions.filter(transaction => {
    // Filter by transaction type
    if (transactionType !== 'all' && transaction.type !== transactionType) {
      return false;
    }
    
    // Filter by date range
    if (dateRange !== 'all') {
      const txnDate = new Date(transaction.transaction_date);
      const today = new Date();
      
      if (dateRange === 'today') {
        return txnDate.toDateString() === today.toDateString();
      } else if (dateRange === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 7);
        return txnDate >= weekAgo;
      } else if (dateRange === 'month') {
        const monthAgo = new Date();
        monthAgo.setMonth(today.getMonth() - 1);
        return txnDate >= monthAgo;
      }
    }
    
    return true;
  });
  
  // Calculate total earnings and withdrawals
  const totalEarnings = transactions
    .filter(txn => txn.type === 'credit')
    .reduce((sum, txn) => sum + parseFloat(txn.amount), 0);
    
  const totalWithdrawals = transactions
    .filter(txn => txn.type === 'debit')
    .reduce((sum, txn) => sum + parseFloat(txn.amount), 0);
  
  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleString(undefined, options);
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <SafeIcon icon={FiClock} className="w-4 h-4 text-yellow-500" />;
      case 'failed':
        return <SafeIcon icon={FiXCircle} className="w-4 h-4 text-red-500" />;
      default:
        return <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const exportTransactions = () => {
    // Create CSV content
    const headers = ['Date', 'Type', 'Amount', 'Description', 'Status'];
    const rows = filteredTransactions.map(txn => [
      formatDate(txn.transaction_date),
      txn.type,
      txn.amount,
      txn.description,
      txn.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Operator Wallet</h1>
          <p className="text-gray-600 mt-2">
            Manage your earnings and transactions
          </p>
        </motion.div>
        
        {/* Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8"
        >
          <Card className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Current Balance */}
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-6 text-white">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-white bg-opacity-20 rounded-full">
                    <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="ml-3 text-lg font-semibold">Current Balance</h3>
                </div>
                <p className="text-3xl font-bold">₹{walletBalance.toFixed(2)}</p>
                <p className="text-sm text-white text-opacity-80 mt-2">
                  Available for application payments and withdrawals
                </p>
              </div>
              
              {/* Total Earnings */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-white bg-opacity-20 rounded-full">
                    <SafeIcon icon={FiArrowDown} className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="ml-3 text-lg font-semibold">Total Earnings</h3>
                </div>
                <p className="text-3xl font-bold">₹{totalEarnings.toFixed(2)}</p>
                <p className="text-sm text-white text-opacity-80 mt-2">
                  Total earnings from completed applications
                </p>
              </div>
              
              {/* Total Spent */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-white bg-opacity-20 rounded-full">
                    <SafeIcon icon={FiArrowUp} className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="ml-3 text-lg font-semibold">Total Spent</h3>
                </div>
                <p className="text-3xl font-bold">₹{Math.abs(totalWithdrawals).toFixed(2)}</p>
                <p className="text-sm text-white text-opacity-80 mt-2">
                  Total spent on application fees and withdrawals
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="flex-1 max-w-xs">
                Add Money
              </Button>
              <Button variant="outline" className="flex-1 max-w-xs">
                Withdraw
              </Button>
            </div>
          </Card>
        </motion.div>
        
        {/* Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Transaction History</h2>
              
              <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiFilter} className="text-gray-500" />
                  <select
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Types</option>
                    <option value="credit">Credits</option>
                    <option value="debit">Debits</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiClock} className="text-gray-500" />
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportTransactions}
                  className="flex items-center"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(transaction.transaction_date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-medium">{transaction.description}</div>
                          {transaction.order_id && (
                            <div className="text-xs text-gray-500">Order ID: <span className="font-mono">{formatOrderId(transaction.order_id)}</span></div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(transaction.status)}
                            <span className="ml-1.5 text-sm capitalize">
                              {transaction.status}
                            </span>
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-right ${
                          transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.type === 'credit' ? '+' : '-'}₹{Math.abs(parseFloat(transaction.amount)).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <SafeIcon icon={FiDollarSign} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No transactions found</p>
                <p className="text-sm text-gray-500 mt-2">
                  {transactionType !== 'all' || dateRange !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Complete applications to start earning'}
                </p>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default OperatorWallet;